/**
 * ButtonSlotControl — Label + URL inputs for button slots.
 *
 * Preserves styleClass, width, height, and appearance from the original slot value.
 */

import { useState, useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { SlotValue } from '@zaviago/storefront-config'

interface ButtonSlotControlProps {
  slotValue: SlotValue
  onChange: (v: SlotValue) => void
  slotLabel: string
}

function extractButton(slot: SlotValue): { label: string; href: string } {
  if (typeof slot === 'string') return { label: slot, href: '' }
  if ('label' in slot) return { label: slot.label, href: slot.href ?? '' }
  return { label: '', href: '' }
}

export function ButtonSlotControl({
  slotValue,
  onChange,
  slotLabel,
}: ButtonSlotControlProps) {
  const [btnLabel, setBtnLabel] = useState(() => extractButton(slotValue).label)
  const [btnHref, setBtnHref] = useState(() => extractButton(slotValue).href)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    const { label, href } = extractButton(slotValue)
    setBtnLabel(label)
    setBtnHref(href)
  }, [slotValue])

  const commit = (nextLabel: string, nextHref: string) => {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      if (typeof slotValue === 'string') {
        onChange({ label: nextLabel, href: nextHref })
      } else if ('label' in slotValue) {
        onChange({ ...slotValue, label: nextLabel, href: nextHref })
      } else {
        onChange({ label: nextLabel, href: nextHref })
      }
    }, 300)
  }

  const handleLabelChange = (v: string) => {
    setBtnLabel(v)
    commit(v, btnHref)
  }

  const handleHrefChange = (v: string) => {
    setBtnHref(v)
    commit(btnLabel, v)
  }

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="space-y-2">
      <Label className="text-[13px] font-medium text-gray-600">{slotLabel}</Label>
      <div className="space-y-1.5">
        <Input
          value={btnLabel}
          onChange={(e) => handleLabelChange(e.target.value)}
          placeholder="Button text"
        />
        <Input
          value={btnHref}
          onChange={(e) => handleHrefChange(e.target.value)}
          placeholder="https://..."
        />
      </div>
    </div>
  )
}
